import { React, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Dialog, DialogContent, DialogTitle, Link, Tooltip, Typography } from "@mui/material";
import GppGoodIcon from "@mui/icons-material/GppGood";
import UseWindowDimensions from "./GetWindowDimensions";
import PhotoViewer from "../components/PhotoViewer";
import './Search.css';
import { getConfig } from "../configService";

const TARGET_WIDTH = 315;
const imageAspectRatio = 1 / 1.618034;
export default function SearchResult(props) {
    const { width } = UseWindowDimensions().width > 700 ? { width: UseWindowDimensions().width/2 } : {width: UseWindowDimensions().width};
    const navigate = useNavigate();
    const [numberOfColumns, setNumberOfColumns] = useState(Math.floor((width - 16) / TARGET_WIDTH));
    const [boxWidth, setBoxWidth] = useState((width - 16) / numberOfColumns - 30);
    const [boxHeight, setBoxHeight] = useState(((width - 16) / numberOfColumns - 60) * imageAspectRatio + 190);
    const [innerBoxWidth, setInnerBoxWidth] = useState(boxWidth - 30);
    const [textBoxWidth, setTextBoxWidth] = useState(boxWidth - 40);
    const [isDescriptionDialogOpen, setIsDescriptionDialogOpen] = useState(false);
    const [isContentOverflowing, setIsContentOverflowing] = useState(false);
    const textContainerRef = useRef(null);

    function setCorrectBoxWidth() {
        let calculatedWidth = (width - 16) / numberOfColumns - 30
        let finalWidth = (props.isBookingPage && calculatedWidth > props.maxWidth) ? props.maxWidth : calculatedWidth;
        setBoxWidth(finalWidth);
    }

    useEffect(() => {
        if (props.setColumnsOnParent) {
            props.setColumnsOnParent(numberOfColumns);
        }
    }, [props.setColumnsOnParent, numberOfColumns])

    useEffect(() => {
        setNumberOfColumns(props.isBookingPage ? 1 : Math.floor((width - 16) / TARGET_WIDTH));
        setCorrectBoxWidth();
    }, [])

    useEffect(() => {
        setBoxHeight((boxWidth - 30) * imageAspectRatio + 190);
    }, [boxWidth, numberOfColumns]);

    useEffect(() => {
        setNumberOfColumns(props.isBookingPage ? 1 : Math.floor((width - 16) / TARGET_WIDTH));
    }, [width])

    useEffect(() => {
        setCorrectBoxWidth();
    }, [width, numberOfColumns])

    useEffect(() => {
        setInnerBoxWidth(boxWidth - 30);
        setTextBoxWidth(boxWidth - 40);
    }, [boxWidth])

    useEffect(() => {
        setIsDescriptionDialogOpen(false);
    }, [props.item?.id]);

    useLayoutEffect(() => {
        const element = textContainerRef.current;
        if (!element || props.isBookingPage) {
            setIsContentOverflowing(false);
            return;
        }

        let frameId = null;
        const measureOverflow = () => {
            frameId = window.requestAnimationFrame(() => {
                const hasOverflow = element.scrollHeight > element.clientHeight + 1;
                setIsContentOverflowing(hasOverflow);
            });
        };

        measureOverflow();

        const canObserveResize = typeof ResizeObserver !== "undefined";
        let observer = null;
        if (canObserveResize) {
            observer = new ResizeObserver(() => {
                measureOverflow();
            });
            observer.observe(element);
        }

        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
            if (observer) {
                observer.disconnect();
            }
        };
    }, [
        props.item?.id,
        props.item?.name,
        props.item?.shortDescription,
        props.item?.city,
        props.item?.state,
        props.item?.servicesProvided,
        textBoxWidth,
        props.isBookingPage
    ]);

    const config = getConfig()
    const listOfImages = props.item.imageList?.map((image) =>
        config.imageBaseUrl + props.item.id + "/" + image + "-medium.jpg");

    return (
        <Box maxWidth={boxWidth} marginBottom={2} display={"block"} overflow={"hidden"} height={"max-content"}
            style={{
                marginLeft: (props.k % numberOfColumns) === 0 ? "auto" : "unset",
                marginRight: ((props.k + 1) % numberOfColumns) === 0 || ((props.k + 1) % numberOfColumns) === 0 || props.isLast ? "auto" : "unset"
            }}
        >
            <Box className="search-result" height={"max-content"} boxShadow={2}>
                <Box display={"flow-root"} width={innerBoxWidth}>
                    <Box width={innerBoxWidth + "px"} position={"relative"} overflow={"hidden"}>
                        <PhotoViewer images={listOfImages} searchResultView={true} innerBoxWidth={innerBoxWidth} boxHeight={boxHeight} />
                    </Box>
                    <Box
                        ref={textContainerRef}
                        sx={{ display: "block", margin: "0px 10px", overflow: "hidden" }}
                        height={props.isBookingPage ? "auto" : "190px"}
                        width={(textBoxWidth) + "px"}
                    >
                        <h3 style={{ marginTop: 0, marginBottom: "0.5em", display: "flex", alignItems: "flex-start", gap: "0.25em" }}>
                            <Link underline={"hover"} onClick={() => {
                                navigate(props.item.friendlyUrl ? "/" + encodeURIComponent(props.item.friendlyUrl) : "/details?id=" + props.item.id)
                            }} onMouseOver={(event) => {
                                event.target.style.cursor = "pointer"
                            }} data-testid="item-name-link">{props.item.name}</Link>
                            {props.item.isMemberProvider && (
                                <Tooltip title="Member provider" arrow>
                                    <GppGoodIcon
                                        data-testid="member-provider-badge"
                                        color="primary"
                                        sx={{ fontSize: "1em", flexShrink: 0, mt: "0.12em" }}
                                    />
                                </Tooltip>
                            )}
                        </h3>
                        <Box display={"flex"}>
                            <Typography style={{ marginRight: ".5em" }}>{props?.item?.rating?.toFixed(1)}</Typography>
                            <div className="stars-outer">
                                <div className="stars-inner"
                                    style={{ width: (Math.round(((props.item.rating / 5) * 67))) }}></div>
                            </div>
                        </Box>
                        <Box className="search-result-description">
                            {(!props.item.shortDescription || props.item.shortDescription.length < 100 || (props.item.servicesProvided && props.item.servicesProvided.length > 0)) && props.item.city && <Typography style={{
                                fontStyle: "italic",
                                marginBottom: ".5em"
                            }}>{props.item.city}, {props.item.state}</Typography>}
                            {props.item.servicesProvided && !props.isBookingPage && <Typography style={{ textAlign: "left" }}>{props.item.servicesProvided.join(" | ")} </Typography>}
                            {(!props.item.servicesProvided || props.item.servicesProvided.length === 0) && props.item.shortDescription && !props.isBookingPage && (
                                <Box>
                                    <Tooltip
                                        title={props.item.shortDescription}
                                        placement="top-start"
                                        arrow
                                        disableHoverListener={!isContentOverflowing}
                                    >
                                        <Typography
                                            style={{
                                                textAlign: "left",
                                                display: "-webkit-box",
                                                WebkitBoxOrient: "vertical",
                                                WebkitLineClamp: 3,
                                                overflow: "hidden"
                                            }}
                                        >
                                            {props.item.shortDescription}
                                        </Typography>
                                    </Tooltip>
                                    {isContentOverflowing && (
                                        <Link
                                            underline="hover"
                                            onClick={() => setIsDescriptionDialogOpen(true)}
                                            onMouseOver={(event) => {
                                                event.target.style.cursor = "pointer";
                                            }}
                                            sx={{ fontSize: "0.85rem" }}
                                        >
                                            More
                                        </Link>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Dialog
                open={isDescriptionDialogOpen}
                onClose={() => setIsDescriptionDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{props.item.name}</DialogTitle>
                <DialogContent>
                    <Typography style={{ whiteSpace: "pre-line" }}>
                        {props.item.shortDescription}
                    </Typography>
                </DialogContent>
            </Dialog>
        </Box>
    )
}
